// src/components/Common/DonateModal.js

import React, { useEffect, useState, useContext } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { QRCodeCanvas } from 'qrcode.react';
import apis from '../../services/api';
import { ToastContext } from '../../contexts/ToastContext';
import { FaClipboard } from 'react-icons/fa';

export default function DonateModal({ isOpen, onClose }) {
  const [cryptoDetails, setCryptoDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState(null);

  const { addToast } = useContext(ToastContext);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
      const fetchCryptoDetails = async () => {
        setLoading(true);
        setError('');
        try {
          const data = await apis.Payment.getCryptoDetails();
          setCryptoDetails(data);
        } catch (err) {
          setError('Failed to load crypto details. Please try again later.');
        }
        setLoading(false);
      };
      fetchCryptoDetails();
    } else {
      document.body.classList.remove('overflow-hidden');
      setSelectedCrypto(null);
    }

    return () => document.body.classList.remove('overflow-hidden');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast('Address copied to clipboard!', 'success');
    } catch (err) {
      addToast('Failed to copy address.', 'error');
    }
  };

  const handleSelectCrypto = (crypto) => {
    if (selectedCrypto && selectedCrypto.cryptoType === crypto.cryptoType) {
      setSelectedCrypto(null);
    } else {
      setSelectedCrypto(crypto);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-md p-4 sm:p-0">
      <div className="relative bg-glassOverlay rounded-2xl p-4 sm:p-6 md:p-8 w-full max-w-lg sm:max-w-4xl animate-zoomIn shadow-lg max-h-screen flex flex-col">
        
        {/* Header with Close Button */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div className="text-center flex-1">
            <h2 className="text-xl sm:text-3xl font-bold text-accentOrange">Thank You for Supporting Us! ❤️</h2>
            <p className="text-gray-300 text-sm sm:text-base">
              Your generosity keeps us running. Select a cryptocurrency to view donation details:
            </p>
          </div>
          <button
            className="ml-4 text-gray-400 hover:text-white transition focus:outline-none flex-shrink-0"
            onClick={onClose}
            aria-label="Close Donate Modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Crypto Details */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <svg
                className="animate-spin h-8 w-8 sm:h-10 sm:w-10 text-accentBlue"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                ></path>
              </svg>
            </div>
          ) : error ? (
            <p className="text-red-500 text-center">{error}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {cryptoDetails.map((crypto, index) => (
                <div
                  key={index}
                  className={`relative bg-gradient-to-br from-blue-900 via-gray-800 to-purple-900 bg-opacity-90 rounded-xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-2xl transform transition-transform duration-300 ${
                    selectedCrypto && selectedCrypto.cryptoType === crypto.cryptoType
                      ? 'col-span-1 sm:col-span-2 lg:col-span-3'
                      : 'cursor-pointer'
                  }`}
                  onClick={() => handleSelectCrypto(crypto)}
                >
                  {/* Crypto Icon and Title */}
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <img
                      src={crypto.icon}
                      alt={`${crypto.cryptoType} Icon`}
                      className="h-8 w-8 sm:h-12 sm:w-12 object-contain rounded-full"
                    />
                    <h3 className="text-lg sm:text-xl font-semibold text-white">{crypto.cryptoType}</h3>
                  </div>

                  {/* Expanded Details */}
                  {selectedCrypto && selectedCrypto.cryptoType === crypto.cryptoType && (
                    <div className="mt-4 sm:mt-6">
                      <div className="bg-gray-700 bg-opacity-50 p-3 sm:p-4 rounded-lg shadow-inner text-sm sm:text-base">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                          {/* Address and Network Info */}
                          <div className="space-y-2">
                            <div>
                              <span className="block text-xs sm:text-sm text-gray-300">Address</span>
                              <div className="flex items-center space-x-2">
                                <span
                                  className="text-white font-mono text-sm sm:text-base break-words cursor-pointer hover:text-accentOrange"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(crypto.address);
                                  }}
                                >
                                  {crypto.address}
                                </span>
                                <button
                                  className="text-black hover:text-accentOrange focus:outline-none"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(crypto.address);
                                  }}
                                  aria-label="Copy Address"
                                >
                                  <FaClipboard className="h-4 w-4 sm:h-5 sm:w-5" />
                                </button>
                              </div>
                            </div>
                            <div>
                              <span className="block text-xs sm:text-sm text-gray-300">Network</span>
                              <span className="text-white font-mono text-sm sm:text-base truncate">
                                {crypto.network}
                              </span>
                            </div>
                          </div>

                          {/* QR Code */}
                          <div className="mt-4 sm:mt-0 sm:ml-4 flex-shrink-0">
                            <QRCodeCanvas value={crypto.address} size={80} />
                          </div>
                        </div>
                      </div>

                      {/* Hide Details Button */}
                      <div className="mt-2 sm:mt-4 flex justify-end">
                        <button
                          className="text-accentOrange hover:text-white font-semibold text-sm sm:text-base"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCrypto(null);
                          }}
                        >
                          Hide Details
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 sm:mt-8 text-center text-gray-300 text-xs sm:text-base">
          <p>Every contribution, big or small, makes a huge impact. 💕</p>
        </div>
      </div>
    </div>
  );
}
